// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';
// eslint-disable-next-line import/no-extraneous-dependencies
import Big from 'big.js';

import { Receipt } from 'shared/order/receipt';

const calculateDiscountsToCart = ({ detailItem, mappedRewards, receiptProduct, runningPrice }) => {
  const { quantity } = detailItem;
  let totalAdditionalCredit = Big(0);
  let discountedPrice = runningPrice;

  const bogoSavings = {
    individual: detailItem?.bogoSavings?.individual ?? [],
    total: detailItem?.bogoSavings?.total ?? Big(0),
  };

  _.forEach(_.keys(mappedRewards), (rewardId) => {
    const reward = mappedRewards[rewardId];
    const { conditionQuantity, discountPercentage, redemptionMultiplier, specialId, specialName } = reward;
    const discountMultiplier = Big(+discountPercentage).div(Big(100));

    /**
     * Compounding interest formula:
     *
     * A = P(1 + r/n)^nt
     *
     * A  =  final amount (amount after all redemptions are applied)
     * P  =  initial principal balance (price before applying the discounts)
     * r  =  interest rate.  (Because we are giving a discount rather than adding interest, we want to subtract from our
     *       initial value.  Therefore, we need the discount multiplier to be negative here.)
     * n  =  number of times interest applied per time period.  (Only apply the redemption once per redemption.)
     * t  =  number of time periods elapsed.  (One period = one redemption)
     */
    const n = Big(1);
    const t = redemptionMultiplier;
    const r = discountMultiplier.times(Big(-1));
    const nt = +n.times(t); // Big.pow doesn't accept Big numbers I guess? So we need to convert to a number here.
    const parenValue = Big(1).add(r.div(n)); // (1 + r/n)
    const newDiscountedPrice = discountedPrice.times(parenValue.pow(nt)); // bring it all together
    const totalDiscountValue = discountedPrice.minus(newDiscountedPrice);
    discountedPrice = newDiscountedPrice;

    const individualDiscountValue = totalDiscountValue.div(quantity);
    const additionalCredit = individualDiscountValue.times(quantity);

    // add a discount to this receipt item
    receiptProduct.addDiscount({
      type: `bogo`,
      value: individualDiscountValue,
      id: specialId,
      name: specialName,
      additionalAttributes: {
        satisfiedBy: [],
      },
    });
    // add the discount to the item
    bogoSavings.individual.push({
      conditionQuantity,
      discountAmount: individualDiscountValue,
      dollarDiscount: +individualDiscountValue.div(100),
      isDiscountToCartReward: true,
      maxApplicable: quantity,
      specialId,
      stackingBehavior: 'compounding',
    });

    bogoSavings.total = bogoSavings.total.add(additionalCredit);
    totalAdditionalCredit = totalAdditionalCredit.add(additionalCredit);
  });

  if (bogoSavings.individual.length > 0) {
    detailItem.bogoSavings = bogoSavings;
  }
  return { runningPrice: discountedPrice, productCredit: totalAdditionalCredit };
};

const calculateTaxes = ({
  bottleDepositTaxCents,
  cannabisTax,
  isVapeTaxed,
  itemSubtotal,
  nonArmsLength,
  receiptProduct,
  runningPrice,
  taxConfig,
  salesTax,
  wholesaleSubtotal,
}) => {
  let priceToTax = Big(0);

  // when tax config is "both," then we want to tax off of the subtotal of the item (base price * quantity)
  if (taxConfig.both) {
    priceToTax = itemSubtotal;
  }

  // When the tax config is "discounts first," we want to tax off of the discounted price
  if (taxConfig.discountsFirst) {
    priceToTax = runningPrice;
  }

  delete receiptProduct.taxes;

  const cannabisTaxCents = Big(cannabisTax(priceToTax, wholesaleSubtotal, nonArmsLength) || 0);
  const salesTaxCents = Big(salesTax(priceToTax, wholesaleSubtotal, nonArmsLength) || 0);

  receiptProduct.addTax({ type: 'bottleDeposit', value: bottleDepositTaxCents });
  receiptProduct.addTax({ type: 'cannabis', value: cannabisTaxCents });
  receiptProduct.addTax({ type: isVapeTaxed ? 'vape' : 'sales', value: salesTaxCents });

  return {
    bottleDepositTaxCents,
    cannabisTaxCents,
    salesTaxCents,
  };
};

const getTaxConfig = (discountTaxOrder) => ({
  both: discountTaxOrder === 'both',
  discountsFirst: discountTaxOrder === 'discountsFirst',
  taxesFirst: discountTaxOrder === 'taxesFirst',
});

// applicableRewards contains information for whether or not a special is a stacking compounding percent off DTC
// lineItems has the discountToCart percentage value
const mapDtcRewards = (lineItems = [], applicableRewards = {}) => {
  const mappedRewards = {};

  const flatRewards = _.uniqBy(_.flatMap(applicableRewards), 'rewardId');
  const specialsHash = _.keyBy(
    _.flatMap(lineItems, ({ specialData }) => specialData?.bogoSpecials ?? []),
    'specialId'
  );

  _.forEach(flatRewards, (reward) => {
    const { conditionQuantity, isSecpoDtc, redemptionMultiplier, rewardId, specialId, dollarDiscount } = reward;
    if (!isSecpoDtc) {
      return;
    }

    const special = specialsHash?.[specialId];
    const discountPercentage = special?.discountToCart.value;
    const { specialName } = special;

    mappedRewards[rewardId] = {
      discountPercentage,
      redemptionMultiplier,
      specialId,
      specialName,
      conditionQuantity,
      dollarDiscount,
    };
  });

  return mappedRewards;
};

const prepareProductData = ({ cartProduct, detailItem, receiptProduct, taxConfig }) => {
  const { basePrice, discounts = {}, mixAndMatch, quantity, taxes } = receiptProduct;
  const { bottleDepositTaxCents, cannabisTax, salesTax, wholesalePrice } = detailItem;
  const { nonArmsLength } = cartProduct.product;

  const itemSubtotal = (mixAndMatch?.adjustedBasePrice || basePrice).times(quantity);
  const wholesaleSubtotal = wholesalePrice.times(quantity);
  const totalTaxes = Receipt.sumSection(taxes);
  const totalDiscounts = Receipt.sumValueSection(discounts);

  let runningPrice = Big(0);
  // when taxes are first, we want to add them to running price before we apply DTC
  if (taxConfig.taxesFirst) {
    runningPrice = itemSubtotal.add(totalTaxes).minus(totalDiscounts);
  }

  // when taxes are not first, we don't want to add any taxes until after we apply DTC, so only subtract the discounts
  if (taxConfig.both || taxConfig.discountsFirst) {
    runningPrice = itemSubtotal.minus(totalDiscounts);
  }

  return {
    bottleDepositTaxCents,
    cannabisTax,
    itemSubtotal,
    nonArmsLength,
    runningPrice,
    salesTax,
    wholesaleSubtotal,
  };
};

/**
 * SECPO = Stacking Enabled Compounding Percentage Off
 *
 * DTC = discount to cart
 */
export const calculateStackingCompoundingPercentDtc = ({
  applicableRewards,
  cartProducts,
  credit,
  detailItems,
  discountTaxOrder,
  receipt,
}) => {
  const { products: receiptProducts } = receipt.details;
  const taxConfig = getTaxConfig(discountTaxOrder);
  const mappedRewards = mapDtcRewards(detailItems, applicableRewards);
  let totalAdditionalCredit = Big(0);

  _.forEach(detailItems, (detailItem) => {
    const cartProduct = _.find(cartProducts, ['product.id', detailItem.id]);
    const receiptProduct = _.find(receiptProducts, ['id', detailItem.id]);

    if (!cartProduct || !receiptProduct) {
      return;
    }

    // get price before applying DTC
    const productData = prepareProductData({ cartProduct, detailItem, receiptProduct, taxConfig });
    let { runningPrice } = productData;
    let productCredit = Big(0);

    ({ runningPrice, productCredit } = calculateDiscountsToCart({
      detailItem,
      mappedRewards,
      receiptProduct,
      runningPrice,
    }));

    if (!taxConfig.taxesFirst) {
      calculateTaxes({
        ...productData,
        isVapeTaxed: detailItem.isVapeTaxed,
        receiptProduct,
        runningPrice,
        taxConfig,
      });
    }

    totalAdditionalCredit = totalAdditionalCredit.add(productCredit);
  });

  return { credit: credit.add(totalAdditionalCredit.div(100)) };
};
